import { useState, useMemo } from 'react';
import { ArchiveIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { BulkAddGroupsDrawer } from '@/components/admin/BulkAddGroupsDrawer';
import { BulkRoleChangeDrawer } from '@/components/admin/BulkRoleChangeDrawer';
import { BulkSelectionActionBar } from '@/components/admin/BulkSelectionActionBar';
import { InviteUserDrawer } from '@/components/admin/InviteUserDrawer';
import { UserDetailsDrawer } from '@/components/admin/UserDetailsDrawer';
import { UserListRow } from '@/components/UserListRow';
import { SearchBar } from '@/components/ui/search-bar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/ToastNotification';
import {
  useAllUserProfiles,
  useUpdateUserRolesBulk,
} from '@/lib/hooks/useUserProfile';
import {
  useBulkAddUserGroupMemberships,
  useUsersGroupMemberships,
} from '@/lib/hooks/useMemberships';
import { useGroups } from '@/lib/hooks/useGroups';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useAuth } from '@/lib/hooks/useAuth';
import { cn } from '@/lib/utils';
import { getInitials } from '@/lib/utils/name.utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import {
  getEffectiveSelectedUserIds,
  getSelectableUserIds,
  groupBulkGroupsForPicker,
} from '@/lib/utils/bulk-edit-users.utils';
import { ManageUsersContent } from '@/content/ManageUsersContent';
import type {
  GroupMembershipBadgeRow,
  GroupWithMemberCount,
  UserProfile,
  UserRole,
} from '@/lib/types/database';
import { FEATURE_FLAGS } from '../../feature-flags.const';

const MAX_VISIBLE_BADGES = 2;

export function ManageUsers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [isBulkRoleDrawerOpen, setIsBulkRoleDrawerOpen] = useState(false);
  const [isBulkGroupsDrawerOpen, setIsBulkGroupsDrawerOpen] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(
    () => new Set()
  );
  const [showArchivedGroupFilters, setShowArchivedGroupFilters] =
    useState(false);
  const [groupFilterSearchQuery, setGroupFilterSearchQuery] = useState('');
  const [bulkGroupIds, setBulkGroupIds] = useState<Set<string>>(
    () => new Set()
  );
  const [bulkGroupSearchQuery, setBulkGroupSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    () => new Set()
  );
  const [bulkRole, setBulkRole] = useState<UserRole | ''>('');

  // Fetch all users
  const { data: allUsers, isLoading } = useAllUserProfiles();
  const { user: currentUser } = useAuth();

  // Group badges for visible rows. Single RPC fetch keyed by visible user ids.
  const visibleUserIds = useMemo(
    () => allUsers?.map((u) => u.id) ?? [],
    [allUsers]
  );
  const { byUser: membershipsByUser } =
    useUsersGroupMemberships(visibleUserIds);
  const { data: allGroups = [], isLoading: isLoadingGroups } = useGroups(true);
  const { toast, showToast, hideToast } = useToastNotification();
  const roleDirectory = useRoleDirectory();
  const bulkUpdateRolesMutation = useUpdateUserRolesBulk();
  const bulkAddGroupsMutation = useBulkAddUserGroupMemberships();

  const activeGroups = useMemo(
    () => allGroups.filter((group) => !group.archived_at),
    [allGroups]
  );

  const groupFilters = useMemo(
    () =>
      showArchivedGroupFilters
        ? allGroups
        : allGroups.filter((group) => !group.archived_at),
    [allGroups, showArchivedGroupFilters]
  );

  const selectedGroupIdList = useMemo(
    () => Array.from(selectedGroupIds),
    [selectedGroupIds]
  );

  const selectedGroupFilters = useMemo(
    () =>
      selectedGroupIdList
        .map((groupId) => allGroups.find((group) => group.id === groupId))
        .filter((group): group is GroupWithMemberCount => !!group),
    [allGroups, selectedGroupIdList]
  );

  // Derive selectedUser from allUsers to ensure it's always up-to-date
  const selectedUser = useMemo(() => {
    if (!selectedUserId || !allUsers) {
      return null;
    }
    return allUsers.find((u) => u.id === selectedUserId) || null;
  }, [selectedUserId, allUsers]);

  // Filter users based on search query, role, and selected groups.
  const filteredUsers = useMemo(() => {
    if (!allUsers) {
      return [];
    }

    let filtered = allUsers;

    // Apply role filter
    if (selectedRole !== 'all') {
      filtered = filtered.filter((user) => user.role === selectedRole);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const name = (user.full_name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const groupNames = (membershipsByUser.get(user.id) ?? []).map((group) =>
          group.display_name.toLowerCase()
        );
        return (
          name.includes(query) ||
          email.includes(query) ||
          groupNames.some((groupName) => groupName.includes(query))
        );
      });
    }

    if (selectedGroupIdList.length > 0) {
      filtered = filtered.filter((user) => {
        const userGroupIds = membershipsByUser
          .get(user.id)
          ?.map((group) => group.group_id);
        return userGroupIds?.some((groupId) => selectedGroupIds.has(groupId));
      });
    }

    return filtered;
  }, [
    allUsers,
    membershipsByUser,
    searchQuery,
    selectedGroupIdList.length,
    selectedGroupIds,
    selectedRole,
  ]);

  // Handle user click
  const handleUserClick = (user: UserProfile) => {
    setSelectedUserId(user.id);
    setIsDrawerOpen(true);
  };

  // Role filter options, sourced from the admin-managed role list
  const roleFilters: Array<{ value: UserRole | 'all'; label: string }> = [
    { value: 'all', label: ManageUsersContent.roleFilter.all },
    ...roleDirectory.options.map((role) => ({
      value: role.value,
      label: role.label,
    })),
  ];

  const selectableUserIds = useMemo(
    () => getSelectableUserIds(filteredUsers, currentUser?.id),
    [filteredUsers, currentUser?.id]
  );

  const selectedUserIdList = useMemo(
    () => Array.from(selectedUserIds),
    [selectedUserIds]
  );

  const effectiveSelectedUserIds = useMemo(
    () => getEffectiveSelectedUserIds(selectedUserIdList, currentUser?.id),
    [selectedUserIdList, currentUser?.id]
  );

  const selectedCount = effectiveSelectedUserIds.length;
  const isBulkActionDrawerOpen = isBulkRoleDrawerOpen || isBulkGroupsDrawerOpen;
  const allSelectableSelected =
    selectableUserIds.length > 0 &&
    selectableUserIds.every((userId) => selectedUserIds.has(userId));

  const handleToggleBulkEdit = () => {
    setIsBulkEditMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedUserIds(new Set());
        setBulkRole('');
        setIsBulkRoleDrawerOpen(false);
        setIsBulkGroupsDrawerOpen(false);
      }
      return next;
    });
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedUserIds(new Set(selectableUserIds));
  };

  const handleClearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const toggleGroupFilter = (groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const removeGroupFilter = (groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  };

  const clearGroupFilters = () => {
    setSelectedGroupIds(new Set());
  };

  const handleToggleArchivedGroupFilters = () => {
    setShowArchivedGroupFilters((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedGroupIds((current) => {
          const activeIds = new Set(activeGroups.map((group) => group.id));
          return new Set([...current].filter((id) => activeIds.has(id)));
        });
      }
      return next;
    });
  };

  const handleOpenBulkGroupsDrawer = () => {
    if (selectedCount === 0) {
      return;
    }
    setBulkGroupIds(new Set());
    setBulkGroupSearchQuery('');
    setIsBulkGroupsDrawerOpen(true);
  };

  const handleOpenBulkRoleDrawer = () => {
    if (selectedCount === 0) {
      return;
    }
    setIsBulkRoleDrawerOpen(true);
  };

  const handleBulkRoleApply = async () => {
    if (!bulkRole || selectedCount === 0) {
      return;
    }

    try {
      await bulkUpdateRolesMutation.mutateAsync({
        userIds: effectiveSelectedUserIds,
        role: bulkRole,
      });
      setIsBulkRoleDrawerOpen(false);
      showToast({
        title: ManageUsersContent.bulkEdit.toast.successTitle,
        description: ManageUsersContent.bulkEdit.toast.successDescription(
          selectedCount,
          getRoleLabelByKey(bulkRole)
        ),
        variant: 'success',
      });
      setSelectedUserIds(new Set());
      setBulkRole('');
    } catch (error) {
      setIsBulkRoleDrawerOpen(false);
      showToast({
        title: ManageUsersContent.bulkEdit.toast.errorTitle,
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update user roles. Please try again.',
        variant: 'error',
      });
      console.error('Failed to update user roles:', error);
    }
  };

  const handleBulkAddGroups = async () => {
    if (selectedCount === 0 || bulkGroupIds.size === 0) {
      return;
    }

    try {
      const result = await bulkAddGroupsMutation.mutateAsync({
        userIds: effectiveSelectedUserIds,
        groupIds: [...bulkGroupIds],
      });
      setIsBulkGroupsDrawerOpen(false);
      setBulkGroupIds(new Set());
      setBulkGroupSearchQuery('');
      setSelectedUserIds(new Set());
      showToast({
        title: ManageUsersContent.bulkEdit.addGroupsDrawer.successTitle,
        description:
          ManageUsersContent.bulkEdit.addGroupsDrawer.successDescription(
            result.memberships_added,
            result.unchanged_memberships
          ),
        variant: 'success',
      });
    } catch (error) {
      showToast({
        title: ManageUsersContent.bulkEdit.addGroupsDrawer.errorTitle,
        description:
          error instanceof Error
            ? error.message
            : 'Failed to add groups. Please try again.',
        variant: 'error',
      });
      console.error('Failed to add groups:', error);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="w-full max-w-4xl mx-auto px-4 pb-8 bg-slate-700">
        <h1 className="text-slate-50 mb-6">{ManageUsersContent.pageTitle}</h1>

        {/* Search Bar */}
        <div className="mb-6 flex gap-2">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={ManageUsersContent.search.placeholder}
            className="flex-1"
          />
          <Button
            className="bg-purple-400 text-white whitespace-nowrap rounded-lg h-12 px-4"
            onClick={() => setIsInviteDrawerOpen(true)}
          >
            <PlusIcon size={20} weight="bold" />
            Add Users
          </Button>
        </div>

        {/* Role Filter */}
        <div className="mb-6">
          <label className="text-small text-slate-400 mb-2 block">
            {ManageUsersContent.roleFilter.label}
          </label>
          <div className="flex gap-2 flex-wrap">
            {roleFilters.map((filter) => (
              <Button
                key={filter.value}
                variant={selectedRole === filter.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRole(filter.value)}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className="text-small text-slate-400">
              {ManageUsersContent.groups.filter.label}
            </label>
            <span className="font-field-42 text-[10px] uppercase text-slate-500">
              {ManageUsersContent.groups.filter.helper}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedGroupFilters.map((group) => (
              <GroupFilterChip
                key={group.id}
                group={group}
                onRemove={() => removeGroupFilter(group.id)}
              />
            ))}
            <GroupFilterPopover
              groups={groupFilters}
              selectedGroupIds={selectedGroupIds}
              searchQuery={groupFilterSearchQuery}
              onSearchQueryChange={setGroupFilterSearchQuery}
              onToggleGroup={toggleGroupFilter}
              isLoading={isLoadingGroups}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full border-slate-500 border-dashed bg-transparent text-slate-300 hover:bg-slate-600"
              onClick={handleToggleArchivedGroupFilters}
            >
              {showArchivedGroupFilters
                ? ManageUsersContent.groups.filter.hideArchived
                : ManageUsersContent.groups.filter.showArchived}
            </Button>
            {selectedGroupFilters.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full text-slate-400 hover:text-slate-50"
                onClick={clearGroupFilters}
              >
                {ManageUsersContent.groups.filter.clear}
              </Button>
            )}
          </div>
        </div>

        {/* User Count */}
        {!isLoading && allUsers && (
          <div className="mb-4 flex items-center justify-between gap-4 text-small text-slate-400">
            <span>
              {isBulkEditMode
                ? ManageUsersContent.bulkEdit.selectionContext(
                    selectedCount,
                    filteredUsers.length
                  )
                : ManageUsersContent.userCount.showing(
                    filteredUsers.length,
                    allUsers.length
                  )}
              {selectedGroupFilters.length > 0 &&
                ` · ${ManageUsersContent.groups.filter.countContext}`}
            </span>
            <div className="flex shrink-0 items-center gap-2">
              {isBulkEditMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSelectAll}
                  className="rounded-full border-slate-500 bg-transparent text-slate-300 hover:bg-slate-600"
                  disabled={
                    selectableUserIds.length === 0 || allSelectableSelected
                  }
                >
                  {ManageUsersContent.bulkEdit.selectAllCount(
                    selectableUserIds.length
                  )}
                </Button>
              )}
              {FEATURE_FLAGS.bulkEditUsers && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-slate-500 bg-transparent text-slate-300 hover:bg-slate-600"
                  onClick={handleToggleBulkEdit}
                >
                  {isBulkEditMode
                    ? ManageUsersContent.bulkEdit.toggleOff
                    : ManageUsersContent.bulkEdit.toggleOn}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* User List */}
        {isLoading ? (
          <div className="space-y-2 mb-6">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="rounded-2xl border border-slate-500 px-4 py-8 text-center text-body text-slate-300 mb-6">
            {selectedGroupFilters.length > 0
              ? 'No users match these group filters.'
              : searchQuery.trim() || selectedRole !== 'all'
                ? ManageUsersContent.emptyStates.noUsersMatchFilter
                : ManageUsersContent.emptyStates.noUsers}
            {selectedGroupFilters.length > 0 && (
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={clearGroupFilters}>
                  {ManageUsersContent.groups.filter.clear}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <ul
            className={cn(
              'bg-slate-600 rounded-xl mb-6 overflow-hidden',
              isBulkEditMode && selectedCount > 0 && 'mb-32'
            )}
          >
            {filteredUsers.map((user) => {
              const userName = user.full_name || user.email || 'Unknown User';
              const isSelf = user.id === currentUser?.id;
              const isSelected = selectedUserIds.has(user.id);

              const userGroups = membershipsByUser.get(user.id) ?? [];

              return (
                <UserListRow
                  key={user.id}
                  name={userName}
                  initials={getInitials(userName)}
                  belowName={
                    <UserRowMeta role={user.role} groups={userGroups} />
                  }
                  className={isSelected ? 'bg-slate-500/40' : ''}
                  onClick={() => {
                    if (isBulkEditMode) {
                      if (!isSelf) {
                        toggleUserSelection(user.id);
                      }
                      return;
                    }
                    handleUserClick(user);
                  }}
                  showChevron={!isBulkEditMode}
                  showCheckbox={isBulkEditMode}
                  checkboxChecked={isSelected}
                  checkboxDisabled={isSelf}
                  checkboxReplacesInitials={isBulkEditMode}
                  onCheckboxChange={(checked) => {
                    setSelectedUserIds((prev) => {
                      const next = new Set(prev);
                      if (checked) {
                        next.add(user.id);
                      } else {
                        next.delete(user.id);
                      }
                      return next;
                    });
                  }}
                />
              );
            })}
          </ul>
        )}

        {isBulkEditMode && !isBulkActionDrawerOpen && (
          <BulkSelectionActionBar
            selectedCount={selectedCount}
            onClear={handleClearSelection}
            onChangeRole={handleOpenBulkRoleDrawer}
            onAddGroups={handleOpenBulkGroupsDrawer}
            disabled={
              bulkUpdateRolesMutation.isPending ||
              bulkAddGroupsMutation.isPending
            }
          />
        )}
      </div>

      {/* User Details Drawer */}
      <UserDetailsDrawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          setIsDrawerOpen(open);
          if (!open) {
            // Clear selected user when drawer closes
            setSelectedUserId(null);
          }
        }}
        user={selectedUser}
      />

      {/* Invite User Drawer */}
      <InviteUserDrawer
        open={isInviteDrawerOpen}
        onOpenChange={setIsInviteDrawerOpen}
      />

      <BulkRoleChangeDrawer
        open={isBulkRoleDrawerOpen}
        onOpenChange={setIsBulkRoleDrawerOpen}
        users={filteredUsers}
        selectedUserIds={effectiveSelectedUserIds}
        selectedRole={bulkRole}
        onSelectedRoleChange={setBulkRole}
        onConfirm={handleBulkRoleApply}
        isSubmitting={bulkUpdateRolesMutation.isPending}
      />

      <BulkAddGroupsDrawer
        open={isBulkGroupsDrawerOpen}
        onOpenChange={setIsBulkGroupsDrawerOpen}
        selectedUserIds={effectiveSelectedUserIds}
        groups={activeGroups}
        selectedGroupIds={bulkGroupIds}
        searchQuery={bulkGroupSearchQuery}
        membershipsByUser={membershipsByUser}
        isLoadingGroups={isLoadingGroups}
        isSubmitting={bulkAddGroupsMutation.isPending}
        onSearchQueryChange={setBulkGroupSearchQuery}
        onSelectedGroupIdsChange={setBulkGroupIds}
        onConfirm={handleBulkAddGroups}
      />

      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </div>
  );
}

interface GroupFilterChipProps {
  group: GroupWithMemberCount;
  onRemove: () => void;
}

function GroupFilterChip({ group, onRemove }: GroupFilterChipProps) {
  const tone = getGroupRoleTone(group.parent_role);
  const archived = !!group.archived_at;

  return (
    <span
      className={cn(
        'inline-flex max-w-[180px] items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[11px]',
        archived
          ? 'border-amber-400/40 bg-amber-400/15 text-amber-300'
          : tone.pillClassName
      )}
    >
      {archived && <ArchiveIcon size={10} weight="bold" />}
      <span className="truncate">{group.display_name}</span>
      <button
        type="button"
        aria-label={`Remove ${group.display_name} filter`}
        onClick={onRemove}
        className="rounded-full text-current opacity-80 hover:opacity-100"
      >
        <XIcon size={11} weight="bold" />
      </button>
    </span>
  );
}

interface GroupFilterPopoverProps {
  groups: GroupWithMemberCount[];
  selectedGroupIds: Set<string>;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onToggleGroup: (groupId: string) => void;
  isLoading: boolean;
}

function GroupFilterPopover({
  groups,
  selectedGroupIds,
  searchQuery,
  onSearchQueryChange,
  onToggleGroup,
  isLoading,
}: GroupFilterPopoverProps) {
  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((group) =>
      group.display_name.toLowerCase().includes(query)
    );
  }, [groups, searchQuery]);

  const sections = useMemo(
    () => groupGroupsForPicker(filteredGroups),
    [filteredGroups]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full border-slate-500 border-dashed bg-transparent text-slate-300 hover:bg-slate-600"
        >
          <PlusIcon size={14} weight="bold" />
          {ManageUsersContent.groups.filter.addGroup}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-80 border-slate-500 bg-slate-700 text-slate-50"
      >
        <SearchBar
          value={searchQuery}
          onChange={onSearchQueryChange}
          placeholder={ManageUsersContent.groups.filter.searchPlaceholder}
          className="mb-3 [&_input]:border-slate-500 [&_input]:bg-slate-600 [&_input]:text-slate-50 [&_input]:placeholder:text-slate-500"
        />
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full rounded-lg bg-slate-600" />
            <Skeleton className="h-10 w-full rounded-lg bg-slate-600" />
          </div>
        ) : sections.length === 0 ? (
          <div className="rounded-lg border border-slate-600 px-3 py-6 text-center text-small text-slate-400">
            {searchQuery.trim()
              ? ManageUsersContent.groups.filter.searchEmpty
              : ManageUsersContent.groups.filter.empty}
          </div>
        ) : (
          <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
            {sections.map((section) => (
              <PickerGroupSection
                key={section.parentRole}
                parentRole={section.parentRole}
                groups={section.groups}
                selectedGroupIds={selectedGroupIds}
                onToggleGroup={onToggleGroup}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface PickerGroupSectionProps {
  parentRole: UserRole;
  groups: GroupWithMemberCount[];
  selectedGroupIds: Set<string>;
  onToggleGroup: (groupId: string) => void;
}

function PickerGroupSection({
  parentRole,
  groups,
  selectedGroupIds,
  onToggleGroup,
}: PickerGroupSectionProps) {
  const tone = getGroupRoleTone(parentRole);

  return (
    <section>
      <span
        className={cn(
          'mb-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[10px] uppercase text-slate-300',
          tone.pillClassName
        )}
      >
        <span className="h-1 w-1 rounded-full bg-current" />
        {getRoleLabelByKey(parentRole)}
      </span>
      <div className="overflow-hidden rounded-lg bg-slate-600">
        {groups.map((group, index) => (
          <button
            key={group.id}
            type="button"
            onClick={() => onToggleGroup(group.id)}
            className={cn(
              'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-slate-500/30',
              index > 0 && 'border-t border-slate-550/40'
            )}
          >
            <Checkbox checked={selectedGroupIds.has(group.id)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-body text-slate-50">
                  {group.display_name}
                </span>
                {!!group.archived_at && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/15 px-1.5 py-0.5 font-field-42 text-[9px] uppercase text-amber-300">
                    <ArchiveIcon size={9} weight="bold" />
                    Archived
                  </span>
                )}
              </div>
              <code className="mt-0.5 block truncate font-mono text-[11px] text-slate-500">
                {group.import_key}
              </code>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function groupGroupsForPicker(groups: GroupWithMemberCount[]) {
  return groupBulkGroupsForPicker(groups);
}

interface UserRowMetaProps {
  role: UserRole | null;
  groups: GroupMembershipBadgeRow[];
}

function UserRowMeta({ role, groups }: UserRowMetaProps) {
  const roleTone = getGroupRoleTone(role);
  // Surface active groups first; archived assignments live in the overflow popover.
  const active = useMemo(() => groups.filter((g) => !g.archived_at), [groups]);
  const archived = useMemo(
    () => groups.filter((g) => !!g.archived_at),
    [groups]
  );

  const visible = active.slice(0, MAX_VISIBLE_BADGES);
  const overflow = [...active.slice(MAX_VISIBLE_BADGES), ...archived];

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={cn(
          'inline-flex rounded-full border px-2 py-0.5 font-field-42 text-[10px] uppercase tracking-wide',
          roleTone.pillClassName
        )}
      >
        {getRoleLabelByKey(role)}
      </span>
      {visible.map((g) => (
        <RowGroupBadge key={g.group_id} group={g} />
      ))}
      {overflow.length > 0 && <OverflowBadge groups={overflow} />}
    </div>
  );
}

interface RowGroupBadgeProps {
  group: GroupMembershipBadgeRow;
}

function RowGroupBadge({ group }: RowGroupBadgeProps) {
  const tone = getGroupRoleTone(group.parent_role);
  const archived = !!group.archived_at;
  return (
    <span
      className={cn(
        'inline-flex max-w-[140px] items-center gap-1 truncate rounded-full border px-2 py-0.5 font-field-42 text-[10px]',
        archived
          ? 'border-amber-400/40 bg-amber-400/15 text-amber-300'
          : tone.pillClassName
      )}
      title={archived ? `${group.display_name} (Archived)` : group.display_name}
    >
      {archived && <ArchiveIcon size={9} weight="bold" />}
      <span className="truncate">{group.display_name}</span>
    </span>
  );
}

interface OverflowBadgeProps {
  groups: GroupMembershipBadgeRow[];
}

function OverflowBadge({ groups }: OverflowBadgeProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center rounded-full border border-slate-500 bg-slate-600 px-2 py-0.5 font-field-42 text-[10px] text-slate-300 hover:bg-slate-500"
        >
          {ManageUsersContent.groups.badgeOverflow(groups.length)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-60 border-slate-500 bg-slate-600 text-slate-50"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="mb-2 font-field-42 text-[10px] uppercase tracking-wide text-slate-400">
          {ManageUsersContent.groups.popoverTitle}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {groups.map((g) => (
            <RowGroupBadge key={g.group_id} group={g} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
